import axios from "@/utils/axios";
import { asArray } from "@/utils/apiResponse";
const API_URL = process.env.VUE_APP_API_URL || "";
const MARKET_PRICE_DAILY_LIMIT = 2;
const MARKET_PRICE_USAGE_KEY = "serpapi_market_price_daily_usage";

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const readMarketPriceUsage = () => {
  try {
    const today = getTodayKey();
    const saved = JSON.parse(localStorage.getItem(MARKET_PRICE_USAGE_KEY) || "null");

    if (!saved || saved.date !== today) {
      return { date: today, count: 0 };
    }

    return {
      date: today,
      count: Number(saved.count) || 0,
    };
  } catch (error) {
    return { date: getTodayKey(), count: 0 };
  }
};

const saveMarketPriceUsage = (usage) => {
  try {
    localStorage.setItem(MARKET_PRICE_USAGE_KEY, JSON.stringify(usage));
  } catch (error) {
    // If localStorage is unavailable, do not block the request.
  }
};

const consumeMarketPriceSearchQuota = () => {
  const usage = readMarketPriceUsage();
  const remaining = Math.max(MARKET_PRICE_DAILY_LIMIT - usage.count, 0);

  if (remaining <= 0) {
    const error = new Error(
      `Limite quotidienne atteinte: ${MARKET_PRICE_DAILY_LIMIT} recherches de prix par jour. Réessayez demain.`
    );
    error.code = "MARKET_PRICE_DAILY_LIMIT_REACHED";
    error.remaining = 0;
    error.limit = MARKET_PRICE_DAILY_LIMIT;
    throw error;
  }

  const nextUsage = {
    date: usage.date,
    count: usage.count + 1,
  };
  saveMarketPriceUsage(nextUsage);

  return {
    limit: MARKET_PRICE_DAILY_LIMIT,
    remaining: Math.max(MARKET_PRICE_DAILY_LIMIT - nextUsage.count, 0),
  };
};

export const getMarketPriceSearchQuota = () => {
  const usage = readMarketPriceUsage();

  return {
    limit: MARKET_PRICE_DAILY_LIMIT,
    used: Math.min(usage.count, MARKET_PRICE_DAILY_LIMIT),
    remaining: Math.max(MARKET_PRICE_DAILY_LIMIT - usage.count, 0),
    date: usage.date,
  };
};

export const isMarketPriceSearchLimitError = (error) =>
  error?.code === "MARKET_PRICE_DAILY_LIMIT_REACHED";

export const getWarehouseData = async () => {
  const response = await axios.get(`${API_URL}/entrepots`);
  return asArray(response.data);
};

export const getWarehouseDataByID = async (id) => {
  const response = await axios.get(`${API_URL}/entrepots/${id}`);
  return response.data;
};

export const createWarehouse = async (data) => {
  const response = await axios.post(`${API_URL}/entrepots`, data);
  return response.data.data;
};

export const updateWarehouse = async (id, data) => {
  const response = await axios.put(`${API_URL}/entrepots/${id}`, data);
  return response.data.data;
};

export const deleteWarehouse = async (id) => {
  const response = await axios.delete(`${API_URL}/entrepots/${id}`);

  return response.data;
};

export const getProductsData = async () => {
  const response = await axios.get(`${API_URL}/produits`);

  return asArray(response.data);
};

export const getProductsDataInStock = async () => {
  const response = await axios.get(`${API_URL}/produits/en-stock`);

  return asArray(response.data);
};

export const getProductsDataWithStock = async () => {
  // Includes all products with aggregated stock info (even when total is 0)
  const response = await axios.get(`${API_URL}/produits/avec-stock`);
  return asArray(response.data);
};

export const createProduct = async (data) => {
  const response = await axios.post(`${API_URL}/produits`, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const searchProductMarketPrice = async ({ referance_produit, nom, prix_achat }) => {
  consumeMarketPriceSearchQuota();

  const response = await axios.post(`${API_URL}/produits/search-market-price`, {
    referance_produit,
    nom,
    prix_achat,
  });

  return response.data.data;
};

export const searchServiceMarketPrice = async ({ reference_service, nom, prix_vente, prix_achat }) => {
  consumeMarketPriceSearchQuota();

  const response = await axios.post(`${API_URL}/services/search-market-price`, {
    reference_service,
    nom,
    prix_vente: prix_vente ?? prix_achat,
  });

  return response.data.data;
};

export const deleteProduct = async (id) => {
  const response = await axios.delete(`${API_URL}/produits/${id}`);
  return response.data;
};

export const updateProduct = async (id, data) => {
  if (data instanceof FormData) {
    if (data.has("_method")) {
      data.delete("_method");
    }
    data.append("_method", "PUT");
    const response = await axios.post(`${API_URL}/produits/${id}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }
  const response = await axios.put(`${API_URL}/produits/${id}`, data);
  return response.data;
};

export const getProductUnitSales = async (params = {}) => {
  const response = await axios.get(`${API_URL}/ventes-unitaires`, {
    params,
  });
  const payload = response && response.data ? response.data.data : null;
  if (payload) {
    return payload;
  }
  return {
    sales: [],
    products: [],
    users: [],
  };
};

export const createProductUnitSale = async (data) => {
  const response = await axios.post(`${API_URL}/ventes-unitaires`, data);
  return response.data;
};

export const updateProductUnitSale = async (id, data) => {
  const response = await axios.put(`${API_URL}/ventes-unitaires/${id}`, data);
  return response.data;
};

export const deleteProductUnitSale = async (id) => {
  const response = await axios.delete(`${API_URL}/ventes-unitaires/${id}`);
  return response.data;
};

export const getCategoriesData = async () => {
  const response = await axios.get(`${API_URL}/service-categories`);
  return asArray(response.data);
};

export const createCategory = async (data) => {
  const response = await axios.post(`${API_URL}/service-categories`, data);
  return response.data.data;
};

export const deleteCategory = async (id) => {
  const response = await axios.delete(`${API_URL}/service-categories/${id}`);
  return response.data.data;
};

export const updateCategory = async (id, data) => {
  const response = await axios.put(`${API_URL}/service-categories/${id}`, data);
  return response.data.data;
};

export const getServicesData = async () => {
  const response = await axios.get(`${API_URL}/services`);
  return asArray(response.data);
};

export const createService = async (data) => {
  const response = await axios.post(`${API_URL}/services`, data);
  return response.data.data;
};

export const deleteService = async (id) => {
  const response = await axios.delete(`${API_URL}/services/${id}`);
  return response.data;
};

export const updateService = async (id, data) => {
  const response = await axios.put(`${API_URL}/services/${id}`, data);
  return response.data.data;
};
